import { PostAuthPasswordResetBody as BaseFormSchema } from "@boundless/bl-api-client/allauth/allauth.zod"
import { usePostAuthPasswordReset } from "@boundless/bl-api-client/allauth/auth"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { z } from "zod"

import { FormInput } from "@/components/form-elements/FormInput"
import { Button, LoadingButton } from "@/components/ui/button"
import { Form } from "@/components/ui/form"
import { isPasswordResetOkError } from "@/lib/account/utils"
import { getAuthClient, type ErrorType } from "@/lib/clients"
import { applyErrorsToForm } from "@/lib/forms/utils"

import type { ErrorResponse } from "@boundless/bl-api-client/allauth/models"

const FormSchema = BaseFormSchema.extend({
  confirmPassword: z.string().min(1, "Please confirm your password"),
}).refine((data) => data.password === data.confirmPassword, {
  path: ["confirmPassword"],
  message: "Passwords do not match",
})

export function ResetPasswordForm({
  resetKey,
  onSuccess,
  ...props
}: React.ComponentPropsWithoutRef<"form"> & {
  resetKey: string
  onSuccess?: () => void
}) {
  const client = getAuthClient()
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      key: resetKey,
      password: "",
      confirmPassword: "",
    },
  })

  const { isPending, mutate: resetPassword } = usePostAuthPasswordReset({
    mutation: {
      onError: (error: ErrorType<ErrorResponse>) => {
        // Check if this is actually a successful password reset (401 when ACCOUNT_LOGIN_ON_PASSWORD_RESET is False)
        if (isPasswordResetOkError(error)) {
          return onSuccess?.()
        }

        applyErrorsToForm(form.setError, error)
      },
    },
  })

  function handleSubmit(data: z.infer<typeof FormSchema>) {
    resetPassword({ client, data: { key: data.key, password: data.password } })
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} {...props}>
        <div className="flex flex-col space-y-4">
          <FormInput
            name="password"
            placeholder="New password"
            type="password"
            autoComplete="new-password"
            className="w-full"
            autoFocus
            required
            disabled={isPending}
          />
          <FormInput
            name="confirmPassword"
            placeholder="Confirm new password"
            type="password"
            autoComplete="new-password"
            className="w-full"
            required
            disabled={isPending}
          />

          {form.formState.errors.root && (
            <p className="text-destructive text-sm font-medium">
              {form.formState.errors.root.message}
            </p>
          )}

          {isPending ? (
            <LoadingButton>Reset Password</LoadingButton>
          ) : (
            <Button type="submit">Reset Password</Button>
          )}
        </div>
      </form>
    </Form>
  )
}
